import { createMongoAbility, type MongoAbility, type RawRuleOf } from '@casl/ability'

/**
 * Permission strings are what roles store and what admins tick in the
 * permission matrix. Grammar:
 *
 *   <subject>:<action>[@own | @team | @branch]
 *
 *   subject  `*` | `<app>.*` | `<app>.<resource>`   e.g. `tms.shipment`
 *   action   `*` | read | create | update | delete | import | export | print | <custom>
 *   @own     only records whose owner field equals the signed-in user
 *   @team    only records of the people under the user at work, all the way down (`Team`), and their own
 *   @branch  only records of the user's home branch (any of the resource's branch fields)
 *
 * Examples: `*` (everything), `tms.*` (all of TMS), `*:read`,
 * `tms.shipment:update@own` (drivers update their own jobs), `tms.receipt:cancel@branch`
 * (a branch lead cancels the receipts of their own branch).
 */
export type Permission = string

export const standardActions = ['read', 'create', 'update', 'delete', 'import', 'export', 'print'] as const
export type StandardAction = (typeof standardActions)[number]

/** Subjects are permission strings, or records tagged with `subject(key, record)` for `@own` checks */
export type AppAbility = MongoAbility

/** What the permission engine needs to know about a resource. */
export interface SubjectInfo {
  subject: string
  app: string
  actions: readonly string[]
  /** Field(s) holding the user who owns the record, for `@own` (a transfer: its sender and its receiver) */
  ownerField?: string | readonly string[]
  /** Fields holding the record's branch, for `@branch` (a waybill: origin and destination) */
  branchFields?: readonly string[]
  /**
   * The field holding the employee a record is about, for `@team` (a leave: `employeeId`; an employee: `id`), so it
   * reaches someone without an account too. Without it `@team` reads the owner fields, against the team's accounts
   */
  teamField?: string
  /**
   * Of someone else's records, the only ones a scope beyond one's own (`@team`, `@branch`) reaches: a cash entry not of
   * their security deposit, which is pay
   */
  othersOnly?: Record<string, unknown>
}

/** Which records a permission covers: all of them, the user's own, their team's, or their branch's */
export type PermissionScope = 'all' | 'own' | 'team' | 'branch'

/**
 * The people under someone at work (their team, `@team`): those whose manager they are (hr.employee.managerId), those
 * under them in turn, all the way down; never themselves. By employee, and by the accounts of those who have one
 */
export interface Team {
  employeeIds: readonly string[]
  userIds: readonly string[]
}

/** Who an ability is built for: their account, home branch (`@branch`) and team (`@team`) */
export interface AbilityUser {
  id: string
  branchId?: string | null
  team?: Team | null
}

const scopes = ['own', 'team', 'branch'] as const

export interface ParsedPermission {
  subject: string
  action: string
  scope: PermissionScope
}

export function parsePermission(raw: Permission): ParsedPermission {
  const [body = '', suffix] = raw.trim().split('@')
  const [subject = '*', action = '*'] = body.split(':')
  return { subject, action, scope: scopes.find((scope) => scope === suffix) ?? 'all' }
}

export function formatPermission({ subject, action, scope }: ParsedPermission): Permission {
  return `${subject}:${action}${scope === 'all' ? '' : `@${scope}`}`
}

function matchSubjects(glob: string, subjects: readonly SubjectInfo[]): readonly SubjectInfo[] | 'all' {
  if (glob === '*') return 'all'
  if (glob.endsWith('.*')) {
    const app = glob.slice(0, -2)
    return subjects.filter((s) => s.app === app)
  }
  return subjects.filter((s) => s.subject === glob)
}

/**
 * Builds the CASL ability for a user. Runs identically on the API (to
 * enforce) and in the browser (to show or hide actions), so both sides
 * always agree.
 */
export function defineAbility(permissions: readonly Permission[], user: AbilityUser, subjects: readonly SubjectInfo[]): AppAbility {
  const rules: RawRuleOf<AppAbility>[] = []
  for (const raw of permissions) {
    const { subject, action, scope } = parsePermission(raw)
    const targets = matchSubjects(subject, subjects)
    for (const target of targets === 'all' ? subjects : targets) {
      // Only actions the resource offers: even `*` cannot create audit entries or delete the company record
      const actions = action === '*' ? target.actions : target.actions.filter((a) => a === action)
      for (const conditions of conditionsFor(scope, target, user)) {
        for (const a of actions) rules.push({ action: a, subject: target.subject, ...(conditions && { conditions }) })
      }
    }
  }
  return createMongoAbility<AppAbility>(rules)
}

/**
 * The record conditions of one permission, OR-ed as separate rules: none for `all`; one per owner
 * field for `@own`; the team's employees in the team field, else their accounts in each owner field, for `@team`; one
 * per branch field for `@branch` — plus the user's own records, so a wider scope always covers a narrower one (a
 * branch's head keeps a transfer sent to them from another branch). A user without a home branch or a team, or a
 * resource without branch or owner fields, gets only their own.
 */
function conditionsFor(scope: PermissionScope, target: SubjectInfo, user: AbilityUser) {
  const owners = typeof target.ownerField === 'string' ? [target.ownerField] : (target.ownerField ?? [])
  const own = owners.map((field) => ({ [field]: user.id }))
  if (scope === 'own') return own.length ? own : [undefined]
  const others = (conditions: Record<string, unknown>[]) => conditions.map((c) => ({ ...c, ...target.othersOnly }))
  if (scope === 'team') return [...others(teamConditions(target, owners, user.team)), ...own]
  if (scope === 'branch')
    return [...others(user.branchId ? (target.branchFields ?? []).map((field) => ({ [field]: user.branchId })) : []), ...own]
  return [undefined]
}

function teamConditions(target: SubjectInfo, owners: readonly string[], team: Team | null | undefined): Record<string, unknown>[] {
  if (target.teamField) return team?.employeeIds.length ? [{ [target.teamField]: { $in: [...team.employeeIds] } }] : []
  return team?.userIds.length ? owners.map((field) => ({ [field]: { $in: [...team.userIds] } })) : []
}

/** A condition's value that is one of several (`@team`'s people) rather than one */
export const isOneOf = (value: unknown): value is { $in: readonly unknown[] } =>
  typeof value === 'object' && value !== null && Array.isArray((value as { $in?: unknown }).$in)

/** Whether a record meets one condition of a scope: each field equal to its value, or to one of `$in` */
export function meets(conditions: Record<string, unknown>, record: Record<string, unknown>) {
  return Object.entries(conditions).every(([field, value]) =>
    isOneOf(value) ? value.$in.includes(record[field]) : record[field] === value,
  )
}

/** Whether a scope (`scopeFor`) reaches a record: all, or any of its conditions met */
export function inScope(scope: 'all' | null | Record<string, unknown>[], record: Record<string, unknown>): boolean {
  if (scope === 'all') return true
  return (scope ?? []).some((conditions) => meets(conditions, record))
}

/**
 * Turns the rules for one action into a data filter for list queries:
 * `'all'` (no restriction), `null` (no access) or a list of equality
 * conditions that are OR-ed together (from `@own` permissions).
 */
export function scopeFor(ability: AppAbility, action: string, subject: string): 'all' | null | Record<string, unknown>[] {
  const rules = ability.rulesFor(action, subject)
  if (rules.length === 0) return null
  // Each distinct condition once: overlapping grants (`@own` and `@branch`) give the same row filter twice
  const conditions = new Map<string, Record<string, unknown>>()
  for (const rule of rules) {
    if (rule.inverted) continue
    if (!rule.conditions) return 'all'
    conditions.set(JSON.stringify(rule.conditions), rule.conditions as Record<string, unknown>)
  }
  return conditions.size > 0 ? [...conditions.values()] : null
}

/**
 * Whether a scope reaches a record through more than the actor's own: all, or a condition other
 * than one of its owner fields being the actor (their branch's, their team's). What may be done to
 * someone else's document — cancelling a transfer already received — and not only to one's own.
 */
export function beyondOwn(
  scope: 'all' | null | Record<string, unknown>[],
  record: Record<string, unknown>,
  ownerFields: readonly string[],
): boolean {
  if (scope === 'all') return true
  const ownOnly = (conditions: Record<string, unknown>) =>
    Object.entries(conditions).every(([field, value]) => ownerFields.includes(field) && !isOneOf(value))
  return (scope ?? []).some((conditions) => !ownOnly(conditions) && meets(conditions, record))
}

/** True when the user may do anything at all inside an app. */
export function canAccessApp(ability: AppAbility, app: string, subjects: readonly SubjectInfo[]): boolean {
  return subjects.some((s) => s.app === app && s.actions.some((a) => ability.can(a, s.subject)))
}
